import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  adminApi,
  Tenant,
} from '../../api/client';
import { useToast } from '../../contexts/ToastContext';
import { InfoTooltip } from '../../components/InfoTooltip';

type TabId = 'overview' | 'keys' | 'templates' | 'usage' | 'billing';

const tabs: Array<{ id: TabId; label: string; icon: string }> = [
  { id: 'overview', label: 'Overview', icon: '📋' },
  { id: 'keys', label: 'API Keys', icon: '🔑' },
  { id: 'templates', label: 'Templates', icon: '📄' },
  { id: 'usage', label: 'Usage', icon: '📈' },
  { id: 'billing', label: 'Billing', icon: '💰' },
];

export function AdminTenantDetailPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  const { data: tenant, isLoading, error } = useQuery({
    queryKey: ['admin', 'tenant', tenantId],
    queryFn: () => adminApi.getTenant(tenantId!),
    enabled: !!tenantId,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-4 w-64 bg-gray-200 rounded animate-pulse" />
        <div className="flex justify-between items-start">
          <div>
            <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
            <div className="h-4 w-80 bg-gray-100 rounded animate-pulse mt-2" />
          </div>
          <div className="h-8 w-20 bg-gray-200 rounded-full animate-pulse" />
        </div>
        <div className="h-10 w-full bg-gray-100 rounded animate-pulse" />
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex gap-4">
              <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
              <div className="h-4 w-48 bg-gray-100 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate('/admin/tenants')} className="text-primary-600 hover:text-primary-800 text-sm">
          ← Back to Tenants
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-800">Tenant not found</h2>
          <p className="text-red-600 mt-1">{error instanceof Error ? error.message : 'Could not load tenant details'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm text-gray-500">
        <Link to="/admin" className="hover:text-gray-700">Admin</Link>
        <span className="mx-2">›</span>
        <Link to="/admin/tenants" className="hover:text-gray-700">Tenants</Link>
        <span className="mx-2">›</span>
        <span className="text-gray-900">{tenant.name}</span>
      </nav>

      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{tenant.name}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {tenant.email} · Plan: {tenant.pricingPlanId} · Since {new Date(tenant.createdAt).toLocaleDateString()}
          </p>
        </div>
        <StatusBadge status={tenant.status} />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-1">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab tenant={tenant} />}
      {activeTab === 'keys' && <ApiKeysTab tenantId={tenantId!} />}
      {activeTab === 'templates' && <TemplatesTab tenantId={tenantId!} />}
      {activeTab === 'usage' && <UsageTab tenantId={tenantId!} />}
      {activeTab === 'billing' && <BillingTab tenantId={tenantId!} tenantName={tenant.name} />}
    </div>
  );
}

// ─── Status Badge ──────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: 'bg-green-100 text-green-800',
    suspended: 'bg-red-100 text-red-800',
    pending: 'bg-yellow-100 text-yellow-800',
  };
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      <span className={`w-2 h-2 rounded-full mr-2 ${
        status === 'active' ? 'bg-green-500' : status === 'suspended' ? 'bg-red-500' : 'bg-yellow-500'
      }`} />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

// ─── Overview Tab ──────────────────────────────────────────

function OverviewTab({ tenant }: { tenant: Tenant }) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: tenant.name,
    email: tenant.email,
    status: tenant.status,
    pricingPlanId: tenant.pricingPlanId,
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Tenant>) =>
      adminApi.updateTenant(tenant.tenantId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenant', tenant.tenantId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      setEditing(false);
      addToast({ type: 'success', message: 'Tenant updated successfully' });
    },
    onError: (error) => {
      addToast({ type: 'error', message: `Failed to update tenant: ${error instanceof Error ? error.message : 'Unknown error'}` });
    },
  });

  const { data: plans } = useQuery({
    queryKey: ['admin', 'pricingPlans'],
    queryFn: () => adminApi.getPricingPlans(),
    enabled: editing,
  });

  const handleSave = () => {
    updateMutation.mutate(formData);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Tenant Information</h2>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="px-4 py-2 text-sm font-medium text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50"
          >
            Edit
          </button>
        ) : (
          <div className="flex space-x-2">
            <button
              onClick={() => { setEditing(false); setFormData({ name: tenant.name, email: tenant.email, status: tenant.status, pricingPlanId: tenant.pricingPlanId }); }}
              className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={updateMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>

      {updateMutation.isError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {updateMutation.error instanceof Error ? updateMutation.error.message : 'Failed to update tenant'}
        </div>
      )}

      <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <dt className="text-sm font-medium text-gray-500">Tenant ID</dt>
          <dd className="mt-1 font-mono text-sm bg-gray-50 p-2 rounded">{tenant.tenantId}</dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500">Name</dt>
          <dd className="mt-1">
            {editing ? (
              <input
                className="input"
                value={formData.name}
                onChange={(e) => setFormData(d => ({ ...d, name: e.target.value }))}
              />
            ) : (
              <span className="text-gray-900">{tenant.name}</span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500">Email</dt>
          <dd className="mt-1">
            {editing ? (
              <input
                type="email"
                className="input"
                value={formData.email}
                onChange={(e) => setFormData(d => ({ ...d, email: e.target.value }))}
              />
            ) : (
              <span className="text-gray-900">{tenant.email}</span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500">
            Status
            <InfoTooltip text="New tenants start as 'Pending'. Change to 'Active' to enable API access." />
          </dt>
          <dd className="mt-1">
            {editing ? (
              <select
                className="input"
                value={formData.status}
                onChange={(e) => setFormData(d => ({ ...d, status: e.target.value }))}
              >
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="pending">Pending</option>
              </select>
            ) : (
              <StatusBadge status={tenant.status} />
            )}
          </dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500">Pricing Plan</dt>
          <dd className="mt-1">
            {editing ? (
              <select
                className="input"
                value={formData.pricingPlanId}
                onChange={(e) => setFormData(d => ({ ...d, pricingPlanId: e.target.value }))}
              >
                <option value={tenant.pricingPlanId}>{tenant.pricingPlanId}</option>
                {plans?.filter(p => p.planId !== tenant.pricingPlanId).map(p => (
                  <option key={p.planId} value={p.planId}>{p.name}</option>
                ))}
              </select>
            ) : (
              <span className="text-gray-900">{tenant.pricingPlanId}</span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500">Created</dt>
          <dd className="mt-1 text-gray-900">{new Date(tenant.createdAt).toLocaleString()}</dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
          <dd className="mt-1 text-gray-900">{new Date(tenant.updatedAt).toLocaleString()}</dd>
        </div>
      </dl>
    </div>
  );
}

// ─── API Keys Tab ──────────────────────────────────────────

function ApiKeysTab({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newApiKeyValue, setNewApiKeyValue] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  const { data: apiKeys, isLoading } = useQuery({
    queryKey: ['admin', 'apikeys', tenantId],
    queryFn: () => adminApi.getApiKeys(tenantId),
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => adminApi.createApiKey(tenantId, { name }),
    onSuccess: (data) => {
      setNewApiKeyValue(data.apiKey || null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'apikeys', tenantId] });
      addToast({ type: 'success', message: 'API key created — copy it now!' });
    },
    onError: (error) => {
      addToast({ type: 'error', message: `Failed to create API key: ${error instanceof Error ? error.message : 'Unknown error'}` });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (keyId: string) => adminApi.revokeApiKey(tenantId, keyId),
    onSuccess: () => {
      setConfirmRevoke(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'apikeys', tenantId] });
      addToast({ type: 'success', message: 'API key revoked' });
    },
    onError: (error) => {
      addToast({ type: 'error', message: `Failed to revoke key: ${error instanceof Error ? error.message : 'Unknown error'}` });
    },
  });

  const rotateMutation = useMutation({
    mutationFn: (keyId: string) => adminApi.rotateApiKey(tenantId, keyId),
    onSuccess: (data) => {
      setNewApiKeyValue(data.newKey.apiKey || null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'apikeys', tenantId] });
      addToast({ type: 'success', message: 'API key rotated — copy the new key!' });
    },
    onError: (error) => {
      addToast({ type: 'error', message: `Failed to rotate key: ${error instanceof Error ? error.message : 'Unknown error'}` });
    },
  });

  const handleCreate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    createMutation.mutate(fd.get('keyName') as string);
  };

  if (isLoading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">
          API Keys
          <InfoTooltip text="API keys are shown only once at creation. Copy immediately. If lost, create a new key and revoke the old one." />
        </h2>
        <button
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
          onClick={() => { setShowCreateModal(true); setNewApiKeyValue(null); }}
        >
          + Create Key
        </button>
      </div>

      {/* Keys table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prefix</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Used</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {apiKeys?.map((key) => (
              <tr key={key.keyId} className={key.status !== 'active' ? 'opacity-50' : ''}>
                <td className="px-4 py-3 font-mono text-sm">{key.keyPrefix}...</td>
                <td className="px-4 py-3 text-sm">{key.name}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    key.status === 'active' ? 'bg-green-100 text-green-800' :
                    key.status === 'deprecated' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {key.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">{new Date(key.createdAt).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleDateString() : 'Never'}</td>
                <td className="px-4 py-3 text-sm space-x-2">
                  {key.status === 'active' && (
                    <>
                      <button
                        onClick={() => rotateMutation.mutate(key.keyId)}
                        disabled={rotateMutation.isPending}
                        className="text-primary-600 hover:text-primary-800"
                      >
                        Rotate
                      </button>
                      <button
                        onClick={() => setConfirmRevoke(key.keyId)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Revoke
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {(!apiKeys || apiKeys.length === 0) && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                  No API keys. Create one to enable API access for this tenant.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Confirm Revoke Dialog */}
      {confirmRevoke && (
        <Modal onClose={() => setConfirmRevoke(null)}>
          <h2 className="text-xl font-bold mb-4">Revoke API Key?</h2>
          <p className="text-gray-600 mb-6">This action cannot be undone. The key will immediately stop working for all API calls.</p>
          <div className="flex justify-end space-x-3">
            <button className="btn btn-secondary" onClick={() => setConfirmRevoke(null)}>Cancel</button>
            <button
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              onClick={() => revokeMutation.mutate(confirmRevoke)}
              disabled={revokeMutation.isPending}
            >
              {revokeMutation.isPending ? 'Revoking...' : 'Revoke Key'}
            </button>
          </div>
        </Modal>
      )}

      {/* Create Key Modal */}
      {showCreateModal && (
        <Modal onClose={() => { setShowCreateModal(false); setNewApiKeyValue(null); }}>
          <h2 className="text-xl font-bold mb-4">{newApiKeyValue ? 'API Key Created' : 'Create API Key'}</h2>
          {newApiKeyValue ? (
            <div className="space-y-4">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-sm text-yellow-800 font-medium mb-2">
                  ⚠️ Copy this key now — you won't be able to see it again!
                </p>
                <code className="block p-2 bg-white border rounded text-sm break-all">
                  {newApiKeyValue}
                </code>
              </div>
              <button
                className="btn btn-primary w-full"
                onClick={() => {
                  navigator.clipboard.writeText(newApiKeyValue);
                  setShowCreateModal(false);
                  setNewApiKeyValue(null);
                }}
              >
                Copy & Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="label">Key Name</label>
                <input name="keyName" className="input" placeholder="Production Key" required />
              </div>
              <div className="flex justify-end space-x-3">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          )}
        </Modal>
      )}
    </div>
  );
}

// ─── Templates Tab ─────────────────────────────────────────

function TemplatesTab({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [confirmUnassign, setConfirmUnassign] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [searchFilter, setSearchFilter] = useState('');

  const { data: assignments, isLoading } = useQuery({
    queryKey: ['admin', 'tenantTemplates', tenantId],
    queryFn: () => adminApi.getTenantTemplates(tenantId),
  });

  const { data: allTemplates } = useQuery({
    queryKey: ['admin', 'templates'],
    queryFn: () => adminApi.getTemplates(),
    enabled: showAssignModal,
  });

  const bulkAssignMutation = useMutation({
    mutationFn: (templateKeys: string[]) => adminApi.bulkAssignTemplates(tenantId, templateKeys),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenantTemplates', tenantId] });
      setShowAssignModal(false);
      setSelectedKeys(new Set());
      setSearchFilter('');
      addToast({ type: 'success', message: `${variables.length} template${variables.length === 1 ? '' : 's'} assigned` });
    },
    onError: (error) => {
      addToast({ type: 'error', message: `Failed to assign templates: ${error instanceof Error ? error.message : 'Unknown error'}` });
    },
  });

  const unassignMutation = useMutation({
    mutationFn: (templateKey: string) => adminApi.unassignTemplate(templateKey, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenantTemplates', tenantId] });
      setConfirmUnassign(null);
      addToast({ type: 'success', message: 'Template unassigned' });
    },
    onError: (error) => {
      addToast({ type: 'error', message: `Failed to unassign template: ${error instanceof Error ? error.message : 'Unknown error'}` });
    },
  });

  if (isLoading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>;
  }

  const assignedKeys = new Set(assignments?.map(a => a.templateKey) ?? []);
  const availableTemplates = allTemplates?.filter(t => !assignedKeys.has(t.templateKey)) ?? [];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">Assigned Templates</h2>
        <button
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
          onClick={() => setShowAssignModal(true)}
        >
          + Assign Template
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Template Key</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Version</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {assignments?.map((a) => (
              <tr key={a.templateKey}>
                <td className="px-4 py-3 font-mono text-sm">{a.templateKey}</td>
                <td className="px-4 py-3 text-sm">{a.templateName}</td>
                <td className="px-4 py-3 text-sm">{a.labelType}</td>
                <td className="px-4 py-3 text-sm">{a.version}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    a.status === 'active' ? 'bg-green-100 text-green-800' :
                    a.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {a.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">{new Date(a.assignedAt).toLocaleDateString()}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => setConfirmUnassign(a.templateKey)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Unassign
                  </button>
                </td>
              </tr>
            ))}
            {(!assignments || assignments.length === 0) && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                  No templates assigned. Assign templates to let this tenant generate labels.
                  <InfoTooltip text="No templates assigned yet. Assign templates to give this tenant access to generate labels for specific retailers." />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Confirm Unassign */}
      {confirmUnassign && (
        <Modal onClose={() => setConfirmUnassign(null)}>
          <h2 className="text-xl font-bold mb-4">Unassign Template?</h2>
          <p className="text-gray-600 mb-6">
            Remove <strong>{confirmUnassign}</strong> from this tenant? They will no longer be able to generate labels with this template.
          </p>
          <div className="flex justify-end space-x-3">
            <button className="btn btn-secondary" onClick={() => setConfirmUnassign(null)}>Cancel</button>
            <button
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              onClick={() => unassignMutation.mutate(confirmUnassign)}
              disabled={unassignMutation.isPending}
            >
              {unassignMutation.isPending ? 'Removing...' : 'Unassign'}
            </button>
          </div>
        </Modal>
      )}

      {/* Assign Template Modal — Multi-Select */}
      {showAssignModal && (
        <Modal onClose={() => { setShowAssignModal(false); setSelectedKeys(new Set()); setSearchFilter(''); }}>
          <h2 className="text-xl font-bold mb-4">Assign Templates</h2>
          {availableTemplates.length === 0 ? (
            <p className="text-gray-500 py-4">All templates are already assigned to this tenant.</p>
          ) : (
            <>
              {/* Search + Select All bar */}
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="text"
                  placeholder="Filter templates..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <button
                  onClick={() => {
                    const filtered = availableTemplates.filter(t =>
                      !searchFilter || t.templateKey.toLowerCase().includes(searchFilter.toLowerCase()) ||
                      (t.labelType ?? '').toLowerCase().includes(searchFilter.toLowerCase())
                    );
                    const allFilteredSelected = filtered.every(t => selectedKeys.has(t.templateKey));
                    const next = new Set(selectedKeys);
                    if (allFilteredSelected) {
                      filtered.forEach(t => next.delete(t.templateKey));
                    } else {
                      filtered.forEach(t => next.add(t.templateKey));
                    }
                    setSelectedKeys(next);
                  }}
                  className="px-3 py-2 text-xs font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 whitespace-nowrap"
                >
                  {availableTemplates.filter(t =>
                    !searchFilter || t.templateKey.toLowerCase().includes(searchFilter.toLowerCase()) ||
                    (t.labelType ?? '').toLowerCase().includes(searchFilter.toLowerCase())
                  ).every(t => selectedKeys.has(t.templateKey))
                    ? 'Deselect All'
                    : 'Select All'}
                </button>
              </div>

              {/* Template list with checkboxes */}
              <div className="max-h-96 overflow-y-auto space-y-1 border border-gray-200 rounded-lg">
                {availableTemplates
                  .filter(t =>
                    !searchFilter || t.templateKey.toLowerCase().includes(searchFilter.toLowerCase()) ||
                    (t.labelType ?? '').toLowerCase().includes(searchFilter.toLowerCase())
                  )
                  .map((t) => (
                  <label
                    key={t.templateKey}
                    className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 ${
                      selectedKeys.has(t.templateKey) ? 'bg-primary-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedKeys.has(t.templateKey)}
                      onChange={() => {
                        const next = new Set(selectedKeys);
                        if (next.has(t.templateKey)) {
                          next.delete(t.templateKey);
                        } else {
                          next.add(t.templateKey);
                        }
                        setSelectedKeys(next);
                      }}
                      className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{t.templateKey}</p>
                      <p className="text-xs text-gray-500">{t.labelType} · v{t.version} · {t.status}</p>
                    </div>
                  </label>
                ))}
              </div>

              {/* Selection count */}
              {selectedKeys.size > 0 && (
                <p className="mt-2 text-sm text-gray-600">
                  {selectedKeys.size} template{selectedKeys.size === 1 ? '' : 's'} selected
                </p>
              )}
            </>
          )}
          <div className="mt-4 flex justify-end space-x-3">
            <button className="btn btn-secondary" onClick={() => { setShowAssignModal(false); setSelectedKeys(new Set()); setSearchFilter(''); }}>Cancel</button>
            {selectedKeys.size > 0 && (
              <button
                onClick={() => bulkAssignMutation.mutate(Array.from(selectedKeys))}
                disabled={bulkAssignMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {bulkAssignMutation.isPending ? 'Assigning...' : `Assign ${selectedKeys.size} Template${selectedKeys.size === 1 ? '' : 's'}`}
              </button>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Usage Tab ─────────────────────────────────────────────

function UsageTab({ tenantId }: { tenantId: string }) {
  const endDate = new Date().toISOString().split('T')[0];
  const startDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  })();

  const { data: usage, isLoading } = useQuery({
    queryKey: ['admin', 'usage', tenantId, startDate, endDate],
    queryFn: () => adminApi.getUsageAnalytics(startDate, endDate, tenantId),
  });

  if (isLoading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>;
  }

  const usageData = usage as {
    period?: { start: string; end: string };
    totalLabels?: number;
    successfulLabels?: number;
    failedLabels?: number;
    uniqueTemplates?: number;
    avgLatencyMs?: number;
    byDay?: Array<{ date: string; count: number }>;
    byTemplate?: Array<{ templateKey: string; count: number; avgLatencyMs?: number }>;
  } | null;

  const totalLabels = usageData?.totalLabels ?? 0;
  const successRate = totalLabels > 0
    ? Math.round(((usageData?.successfulLabels ?? 0) / totalLabels) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard label="Total Labels" value={totalLabels.toLocaleString()} />
        <SummaryCard label="Success Rate" value={`${successRate}%`} />
        <SummaryCard label="Avg Latency" value={`${usageData?.avgLatencyMs?.toFixed(0) ?? 0}ms`} />
        <SummaryCard label="Templates Used" value={`${usageData?.uniqueTemplates ?? 0}`} />
      </div>

      {/* Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">30-Day Usage</h3>
        {usageData?.byDay && usageData.byDay.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={usageData.byDay}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(val: string) => {
                  const d = new Date(val);
                  return `${d.getMonth() + 1}/${d.getDate()}`;
                }}
                fontSize={12}
              />
              <YAxis fontSize={12} />
              <Tooltip labelFormatter={(val: string) => new Date(val).toLocaleDateString()} />
              <Area type="monotone" dataKey="count" stroke="#4F46E5" fill="#4F46E5" fillOpacity={0.15} name="Labels" />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-[280px] text-gray-400">
            <div className="text-center">
              <p className="text-4xl mb-2">📈</p>
              <p>No usage data for this period</p>
            </div>
          </div>
        )}
      </div>

      {/* Usage by template */}
      {usageData?.byTemplate && usageData.byTemplate.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Usage by Template</h3>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Template</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Labels</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Latency</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {usageData.byTemplate.map((t) => (
                <tr key={t.templateKey}>
                  <td className="px-4 py-3 font-mono text-sm">{t.templateKey}</td>
                  <td className="px-4 py-3 text-sm">{t.count.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm">{t.avgLatencyMs?.toFixed(0) ?? '-'}ms</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Billing Tab ───────────────────────────────────────────

function BillingTab({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const { addToast } = useToast();
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const { data: billing, isLoading } = useQuery({
    queryKey: ['admin', 'billing', tenantId, month],
    queryFn: () => adminApi.getTenantBilling(tenantId, month),
  });

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const result = await adminApi.getBillingExport(month, format, tenantId);
      const blob = new Blob(
        [typeof result === 'string' ? result : JSON.stringify(result, null, 2)],
        { type: format === 'csv' ? 'text/csv' : 'application/json' }
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `billing-${tenantName}-${month}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed', err);
      addToast({ type: 'error', message: `Export failed: ${err instanceof Error ? err.message : 'Unknown error'}` });
    }
  };

  // Generate month options for last 6 months
  const monthOptions = Array.from({ length: 6 }, (_, i) => {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-semibold text-gray-900">Billing</h2>
          <select
            className="input w-auto"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          >
            {monthOptions.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => handleExport('csv')}
            className="px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            ↓ CSV
          </button>
          <button
            onClick={() => handleExport('json')}
            className="px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            ↓ JSON
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>
      ) : billing ? (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SummaryCard label="Total Labels" value={(billing.totalLabels ?? 0).toLocaleString()} />
            <SummaryCard label="Line Items" value={`${billing.lineItems?.length ?? 0}`} />
            <SummaryCard label="Period" value={billing.period ?? month} />
            <SummaryCard
              label="Total Charges"
              value={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(billing.totalAmount ?? 0)}
            />
          </div>

          {/* Line items */}
          {billing.lineItems && billing.lineItems.length > 0 && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Template</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Labels</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {billing.lineItems.map((item, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-3 font-mono text-sm">{item.templateKey}</td>
                      <td className="px-4 py-3 text-sm">{item.labelType}</td>
                      <td className="px-4 py-3 text-sm">{item.labelCount.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm">${(item.rate / 100).toFixed(4)}</td>
                      <td className="px-4 py-3 text-sm font-medium">
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(item.amount / 100)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400">
          <p className="text-4xl mb-2">💰</p>
          <p>No billing data for {month}</p>
        </div>
      )}
    </div>
  );
}

// ─── Shared components ─────────────────────────────────────

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <dt className="text-sm font-medium text-gray-500 truncate">{label}</dt>
      <dd className="mt-1 text-xl font-bold text-gray-900">{value}</dd>
    </div>
  );
}

function Modal({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}
